import { useEffect, useState } from 'react';
import { useGetCurriculumList } from './useGetCurriculumList';
import { registerForResource } from '@academy/utils';

import type { ILesson } from '@academy/graphql/types';

/**
 * Custom hook to handle lesson registration and loading state
 *
 * @param lesson - The lesson data
 * @param courseUrl - The URL of the course
 * @param loggedIn - Whether the user is logged in
 * @param resourceType - The type of resource
 * @returns Object containing isLoading and error states
 */
export const useLessonRegistration = (
  lesson: ILesson | null,
  courseUrl: string | null,
  loggedIn: boolean,
  resourceType: string,
) => {
  const hasLesson = lesson !== null;
  const [isLoading, setIsLoading] = useState(lesson?.contentUrl ? false : hasLesson);
  const [error, setError] = useState(!hasLesson);
  const curriculumList = useGetCurriculumList(lesson?.course?.thirdPartyId || '');
  useEffect(() => {
    if (!hasLesson) {
      return;
    }

    // If we have a content url, we know it's not a quiz
    if (lesson?.contentUrl) {
      return;
    }

    // We don't have a content url and registration is required
    // Let's check if this is a quiz
    // We have to call getCurriculumList to determine if this is a quiz
    // @TODO: ingest the lesson type into the API so we know this at build time
    const curriculumListLesson = curriculumList[lesson?.thirdPartyId || ''];
    if (isLoading && curriculumListLesson) {
      const isQuiz = curriculumListLesson.icon === 'QUIZ';
      const isRegistered = Object.keys(curriculumListLesson).includes('isCompleted');

      if (!isQuiz) {
        setIsLoading(false);
        return;
      }

      if (isQuiz && lesson?.course?.registrationRequired && lesson?.course?.registrationUrl) {
        if (!loggedIn) {
          window.location.assign(`${window.location.origin}${courseUrl}`);
          return;
        }

        if (!isRegistered) {
          const { thirdPartyId: resourceId, registrationUrl } = lesson.course;
          registerForResource({
            registrationUrl,
            resourceId: resourceId,
            resourceType,
          }).then((success) => {
            if (success) {
              setTimeout(() => {
                setIsLoading(false);
              }, 1000);
            } else {
              setError(true);
              setIsLoading(false);
            }
          });
        } else {
          setIsLoading(false);
        }
      }
    }

  }, [courseUrl, curriculumList, hasLesson, isLoading, lesson, loggedIn, resourceType]);

  return { isLoading, error };
}; 
